"use client";

import React, { useEffect, useRef, useState } from "react";
import { ClipboardCheck, PackageOpen, SearchCheck, Sparkles, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  title: "Initial Inquiry",
  description: "Reach out to our team with basic details about your MSP. We maintain strict confidentiality while gathering the high-level data needed to begin the matching process.",
  icon: ClipboardCheck
}, {
  title: "Valuation Review",
  description: "Our experts analyze your financials and operational metrics to provide a realistic market valuation, ensuring you understand your business's true worth before meeting buyers.",
  icon: PackageOpen
}, {
  title: "Buyer Matching",
  description: "We leverage our extensive network of qualified IT and MSP buyers to find the perfect strategic fit, prioritizing those who value your specific service model and culture.",
  icon: Sparkles
}, {
  title: "Introduction Call",
  description: "Once a match is identified, we facilitate a direct introduction. You'll speak with vetted buyers ready to discuss terms, all while avoiding traditional broker fees.",
  icon: SearchCheck
}];
export default function ThePerfectProtocol() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height;
      const currentScrollPosition = windowHeight / 2 - rect.top;
      let percentage = currentScrollPosition / totalScrollableDistance * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="222853" id="the-matching-process" ref={sectionRef} className="bg-background text-foreground relative min-h-screen">
      {}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border py-4 px-6 shadow-sm">
        <div className="max-w-4xl mx-auto flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-default font-semibold text-foreground">
              The Matching Process
            </h2>
            <span className="text-sm font-mono text-primary">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-1.5 bg-secondary" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <div className="text-center mb-24">
          <h3 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            A Streamlined Path <br className="hidden md:block" /> to Your Next Chapter.
          </h3>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default leading-relaxed">
            We've simplified MSP acquisitions. Our four-step framework connects you with serious buyers quickly, transparently, and at a fraction of the cost of traditional brokers.
          </p>
        </div>

        {}
        <div className="relative border-l-2 border-primary/20 ml-4 md:ml-12 space-y-32 pb-24">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <div key={i} data-index={i} className="relative pl-8 md:pl-20 min-h-[30vh] flex flex-col justify-center group">
                {}
                <div className="absolute -left-[11px] top-1/2 -translate-y-1/2 size-5 rounded-md bg-background border-4 border-primary transition-transform duration-500 group-hover:scale-125 shadow-sm" />
                
                {}
                <span className="absolute -left-8 md:-left-12 top-1/2 -translate-y-1/2 text-[8rem] md:text-[14rem] font-default font-bold text-primary/5 select-none leading-none pointer-events-none transition-colors duration-500 group-hover:text-primary/10">
                  {i + 1}
                </span>

                <Card className="relative z-10 border-none bg-transparent overflow-visible">
                  <CardHeader className="px-0 pt-0 pb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="p-3 rounded-md bg-primary text-primary-foreground shadow-md transition-transform duration-300 group-hover:scale-110">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="text-2xl md:text-3xl font-default font-bold text-foreground">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    <p className="text-muted-foreground text-lg leading-relaxed max-w-xl font-default">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-12 text-center relative z-20">
          <Button asChild size="lg" className="rounded-md px-8 bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300 shadow-lg hover:shadow-xl">
            <Link to="/HomePage">
              Start Your Valuation
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}